package com.eladfinish.bible.contest.screens.openQuestions.model

import com.eladfinish.bible.contest.common.usecases.RemoveGodNamesUseCase
import com.eladfinish.bible.contest.di.getKoinInstance
import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable

@Serializable
data class OpenQuestionEntry(
    @SerialName("שאלה") val question: String,
    @SerialName("תשובה") val answer: String,
    @SerialName("ספר") val book: String,
    @SerialName("פרק") val chapter: String,
    @SerialName("פסוק") val verse: String,
    @SerialName("מחבר") val author: String,
    @SerialName("ביטוי-רגולרי") val regex: String,
    @SerialName("אישי") val personal: String,
    @SerialName("גודל") val size: String,
)

@Serializable
data class QuestionItemJson(
    @SerialName("שאלה") var question: String? = "",
    @SerialName("תשובה") var answer: String? = "",
    @SerialName("ספר") var book: String? = "",
    @SerialName("פרק") var chapter: String? = "",
    @SerialName("פסוק") var verse: String? = "",
    @SerialName("מחבר") var author: String? = "",
    @SerialName("ביטוי-רגולרי") var regex: String? = "",
    var id: Int = 0,
) {

    private val removeGodNamesUseCase by lazy { getKoinInstance<RemoveGodNamesUseCase>() }

    private companion object {
        /** Shown as the answer of a question answered by its regex (long-press runs the search). */
        const val SEARCH_FOR_ANSWER_HINT = "לחיצה ארוכה ⬅ \uD83D\uDD0D"
    }

    constructor(
        question: String,
        answer: String,
        author: String,
    ) : this(
        question = question,
        answer = answer,
        author = author,
        book = ""
    )

    constructor(item: OpenQuestionEntry) : this() {
        question = removeGodNamesUseCase(item.question)
        answer = item.answer
        book = item.book
        chapter = item.chapter
        verse = item.verse
        author = item.author
        regex = item.regex

        setRegexAndAnswer(item.question)
    }

    constructor(list: List<Pair<String, String>>, index: Int) : this() {
        val row = list.toOpenQuestionRow()
        question = row.question
        answer = row.answer
        book = row.book
        chapter = row.chapter
        verse = row.verse
        author = row.author
        regex = row.regex
        if (row.answerIsSearch) answer = SEARCH_FOR_ANSWER_HINT
        id = index
    }

    private fun setRegexAndAnswer(question: String) {
        regex = resolveQuestionRegex(regex.orEmpty(), question)
        if (regex?.isNotEmpty() == true && answer?.isEmpty() == true) {
            answer = SEARCH_FOR_ANSWER_HINT
        }
    }

    // למה מוסיפים 3?
    // שורה ראשונה בטבלה היא שורת כותרת: שאלה, תשובה וכו׳
    // שורה שנייה בטבלה היא שורת אפסים (לצורך הפרדה)
    // הפונקציה withIndex ממספרת את הרשימה החל מאפס
    val adjustedId get() = id + 3
}

@Serializable
data class QuestionItemJsonVisibility(
    val questionItemJson: QuestionItemJson,
    val answerIsVisible: Boolean,
)

@Serializable
data class LinkTopicItemEntry(
    @SerialName("ספר") var book: String = "",
    @SerialName("שפה") var language: String? = "",
    @SerialName("גיליון") var sheet: String = "",
    @SerialName("מזהה") var id: String = "",
    @SerialName("קישור") var link: String = "",
) {

    constructor(list: List<Pair<String, String>>) : this() {
        list.forEach { (column, value) ->
            when (column.trim()) {
                "ספר" -> book = value
                "שפה" -> language = value
                "גיליון" -> sheet = value
                "מזהה" -> id = value
                "קישור" -> link = value
            }
        }
    }
}

@Serializable
data class SheetNames(
    @SerialName("sheet_names") val sheetNamesList: List<String>,
) {
    val numberedSheetNames: List<String>
        get() = sheetNamesList.mapIndexed { index, name -> "${(index + 1)}. $name" }
}
